import { useEffect, useMemo, useState } from 'react';
import { metaApi, type MiniItem, type Person, type PersonCredit } from '../api';
import { useInLibrary } from '../meta';
import { navigate } from '../router';
import { DiscoverRow } from '../components/MetaBits';
import { PinButton } from '../components/PinButton';

/**
 * Acteur of regisseur: foto, bio en filmografie. Wat in je bibliotheek staat opent
 * de Plex-pagina, de rest kun je aanvragen via Seerr.
 */
export function PersonPage({ id, lookup }: { id: number | null; lookup: string }) {
  const [person, setPerson] = useState<Person | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    (async () => {
      let pid = id;
      if (!pid && lookup) {
        const r = await metaApi.personLookup(lookup);
        if (!r.id) throw new Error(`“${lookup}” niet gevonden bij TMDB.`);
        navigate(`/person/${r.id}`, { replace: true });
        return;
      }
      if (!pid) throw new Error('Persoon niet gevonden.');
      const p = await metaApi.person(pid);
      if (live) setPerson(p);
    })().catch((e: unknown) => live && setError(e instanceof Error && e.message ? e.message : 'Kon deze persoon niet laden.'));
    return () => {
      live = false;
    };
  }, [id, lookup]);

  if (error) return <main className="page"><p className="placeholder-row error" role="alert">{error === 'not_configured' ? 'Voor acteurspagina’s is een TMDB-sleutel nodig.' : error}</p></main>;
  if (!person) return <main className="page" aria-busy="true"><p className="placeholder-row">Laden…</p></main>;
  return <PersonView p={person} />;
}

function toMini(c: PersonCredit): MiniItem {
  return { type: c.type, tmdbId: c.tmdbId, title: c.title, year: c.year, poster: c.poster, backdrop: c.backdrop, sub: [c.year, c.role].filter(Boolean).join(' · ') };
}

function age(birth: string, until?: string | null): number {
  const b = new Date(birth);
  const e = until ? new Date(until) : new Date();
  let a = e.getFullYear() - b.getFullYear();
  if (e.getMonth() < b.getMonth() || (e.getMonth() === b.getMonth() && e.getDate() < b.getDate())) a -= 1;
  return a;
}

function dateNl(d: string): string {
  return new Date(d).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
}

function PersonView({ p }: { p: Person }) {
  const [bioOpen, setBioOpen] = useState(false);

  const rows = useMemo(() => {
    const byDate = (a: PersonCredit, b: PersonCredit) => (b.date ?? '9999').localeCompare(a.date ?? '9999');
    const released = (c: PersonCredit) => !!c.date;
    // "Bekend van": veel stemmen, eigen rol (geen gastrolletjes van 1 aflevering)
    const known = [...p.credits]
      .filter((c) => released(c) && !(c.type === 'tv' && c.role && /^1 afl/.test(c.role)))
      .sort((a, b) => b.votes - a.votes)
      .slice(0, 20);
    const directing = p.credits.filter((c) => c.job === 'directing').sort(byDate);
    const acting = p.credits.filter((c) => c.job === 'cast');
    return {
      known,
      directing,
      movies: acting.filter((c) => c.type === 'movie').sort(byDate).slice(0, 40),
      tv: acting.filter((c) => c.type === 'tv').sort(byDate).slice(0, 40),
      other: p.credits.filter((c) => c.job === 'writing' || c.job === 'production' || c.job === 'other').sort(byDate).slice(0, 30),
      // Voor "In je bibliotheek": de meest bekende titels nakijken
      check: [...p.credits].filter(released).sort((a, b) => b.popularity - a.popularity).slice(0, 80),
    };
  }, [p]);

  const checkItems = useMemo(() => rows.check.map(toMini), [rows]);
  const found = useInLibrary(checkItems);
  const owned = checkItems.filter((m) => typeof found[`${m.type}:${m.tmdbId}`] === 'string');

  const facts: string[] = [];
  if (p.knownFor) facts.push(p.knownFor);
  if (p.birthday) facts.push(p.deathday ? `${dateNl(p.birthday)} – ${dateNl(p.deathday)} (${age(p.birthday, p.deathday)})` : `Geboren ${dateNl(p.birthday)} (${age(p.birthday)})`);
  if (p.placeOfBirth) facts.push(p.placeOfBirth);

  const longBio = (p.biography?.length ?? 0) > 600;

  return (
    <main className="page person-page">
      <section className="person-head">
        <div className="person-photo" style={p.photo ? { backgroundImage: `url("${p.photo}")` } : undefined} aria-hidden="true">
          {!p.photo && p.name.slice(0, 1)}
        </div>
        <div className="person-info">
          <h1>{p.name}</h1>
          {facts.length > 0 && <div className="subtle person-facts">{facts.join(' · ')}</div>}
          {p.biography && (
            <>
              <p className={`person-bio ${longBio && !bioOpen ? 'is-clamped' : ''}`}>{p.biography}</p>
              <div className="hero-actions" data-focus-group>
                {longBio && <button className="btn btn-sm" data-autofocus onClick={() => setBioOpen((v) => !v)}>{bioOpen ? 'Minder' : 'Meer lezen'}</button>}
                {p.imdbId && <a className="btn btn-sm" href={`https://www.imdb.com/name/${p.imdbId}/`} target="_blank" rel="noreferrer">IMDb</a>}
                <PinButton title={`Met ${p.name}`} source={{ kind: 'tmdb', list: 'person', media: 'all', refId: p.id, refName: p.name }} />
              </div>
            </>
          )}
          <div className="subtle">{p.credits.length} titels · {owned.length > 0 ? `${owned.length} in je bibliotheek` : 'nog niets in je bibliotheek gevonden'}</div>
        </div>
      </section>

      {owned.length > 0 && <DiscoverRow title="In je bibliotheek" items={owned} />}
      {rows.known.length > 0 && <DiscoverRow title="Bekend van" items={rows.known.map(toMini)} />}
      {rows.directing.length > 0 && <DiscoverRow title="Regie" items={rows.directing.map(toMini)} />}
      {rows.movies.length > 0 && <DiscoverRow title="Films" items={rows.movies.map(toMini)} />}
      {rows.tv.length > 0 && <DiscoverRow title="Series" items={rows.tv.map(toMini)} />}
      {rows.other.length > 0 && <DiscoverRow title="Achter de schermen" items={rows.other.map(toMini)} />}
      <div className="footer-space" />
    </main>
  );
}
